import React, { useEffect, useState, useContext } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { ClipboardList } from "lucide-react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useDashboard } from "../Dashboard/Hooks/useDashboard";
import { getStatusIconAndColor, TASK_STATUS_LABELS } from "../Sites/Constants";
import { formatAmountINR } from "../Dashboard/Constants";
import { formatTimeAMPM } from "../../Utils/timeUtils";
import { formatDateDDMMYYYY, toProperCase } from "../../Utils";
import { UserContext } from "../../Context/UserContext";
import {
  AccessDenied,
  FilterCard,
  GenericTable,
  Pagination,
  Spinner,
  RoleValidator,
  Tooltip,
} from "../../Components/Common";
import { ROLES } from "../../Constants/Strings";
import { STATUS } from "../Bills/constants";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import {
  APPROVAL_ATTENDANCE_COLUMNS,
  APPROVAL_ATTENDANCE_LABELS,
  APPROVAL_BILL_COLUMNS,
  APPROVAL_BILL_LABELS,
  APPROVAL_TABS,
  TASK_COLUMNS,
  TASK_LABELS,
  TOAST_MESSAGES,
} from "./constants";
import { useProjects } from "../Project/Hooks/useProjects";
import { getSites } from "../../Services/siteServices";
import { updateBillStatus } from "../../Services/billsService";
import { reviewAttendance } from "../../Services/attendanceService";
import { taskAction } from "../../Services/siteServices";

const ApprovalListPage = () => {
  const { projects, fetchProjects } = useProjects();
  const [activeTab, setActiveTab] = useState(APPROVAL_TABS.BILLS.value);
  const [approvalDetails, setApprovalDetails] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    count: 0,
  });
  const [loading, setLoading] = useState(true);
  const { fetchApprovals } = useDashboard();
  const { user } = useContext(UserContext);
  const [filters, setFilters] = useState({});
  const [sites, setSites] = useState([]);
  const isRestrictedRole = [
    ROLES.MANAGING_DIRECTOR,
    ROLES.HEAD_OFFICE,
    ROLES.GOVERNMENT_OFFICER,
  ].includes(user.role);

  const fetchData = async (params) => {
    setLoading(true);
    try {
      const { page = 1, limit = 10 } = params;
      const offset = (page - 1) * limit;
      const res = await fetchApprovals({
        type: activeTab,
        offset,
        limit,
        ...filters,
      });
      if (res) {
        setApprovalDetails(res.results);
        setPagination((prev) => ({
          ...prev,
          page,
          limit,
          count: res.count || 0,
        }));
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.FETCH_APPROVALS_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await getSites();
      if (response.status === 200) {
        setSites(response.data.results || []);
        const formatted = (response.data.results || []).map((site) => ({
          value: site.id,
          label: site.site_name,
        }));
        setSites(formatted);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.SITES_FETCH_FAILED;
      toast.error(msg);
    }
  };

  const handleBillApproval = async (id, action) => {
    try {
      const res = await updateBillStatus(id, { action });
      if (res.status === 200) {
        toast.success(`Bill ${action}d successfully`);
        fetchData({ page: pagination.page, limit: pagination.limit });
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FETCH_APPROVALS_FAILED;
        toast.error(msg);
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.FETCH_ERROR;
      toast.error(msg);
    }
  };

  const handleAttendanceApproval = async (id, action) => {
    try {
      const res = await reviewAttendance(id, { action });
      if (res.status === 200) {
        toast.success(`Attendance ${action} successfully`);
        fetchData({ page: pagination.page, limit: pagination.limit });
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FETCH_APPROVALS_FAILED;
        toast.error(msg);
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.FETCH_ERROR;
      toast.error(msg);
    }
  };

  const handleTaskApproval = async (id, action) => {
    try {
      const res = await taskAction(id, { action });
      if (res.status === 200) {
        toast.success(`Task ${action} successfully`);
        fetchData({ page: pagination.page, limit: pagination.limit });
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FETCH_APPROVALS_FAILED;
        toast.error(msg);
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.FETCH_ERROR;
      toast.error(msg);
    }
  };

  useEffect(() => {
    if (isRestrictedRole) return;
    fetchProjects();
    fetchSites();
  }, []);

  useEffect(() => {
    if (isRestrictedRole) return;
    fetchData({
      page: pagination.page,
      limit: pagination.limit,
      ...filters,
    });
  }, [pagination.page, pagination.limit, activeTab, filters]);

  if (user.role === ROLES.SITE_ENGINEER) {
    return <AccessDenied />;
  }

  const renderFields = (key, rowData) => {
    switch (key) {
      case "category":
        return toProperCase(rowData.category);
      case "status":
        return (
          <div className={`flex items-center gap-2`}>
            {TASK_STATUS_LABELS[rowData.status]}
          </div>
        );
      case "amount":
        return formatAmountINR(rowData.amount);
      case "date":
        return formatDateDDMMYYYY(rowData.date);
      case "checkin_time":
        return rowData?.events?.[0]
          ? formatTimeAMPM(rowData.events[0].time)
          : "-";
      case "checkout_time":
        return rowData?.events?.[1]
          ? formatTimeAMPM(rowData.events[1].time)
          : "-";
      case "type":
        return rowData.super_task ? "Sub-task" : "Task";
      case "actions":
        if (
          [
            APPROVAL_TABS.BILLS.value,
            APPROVAL_TABS.ATTENDANCE.value,
            APPROVAL_TABS.TASKS.value,
          ].includes(activeTab)
        ) {
          const handleAction =
            activeTab === APPROVAL_TABS.BILLS.value
              ? handleBillApproval
              : activeTab === APPROVAL_TABS.ATTENDANCE.value
              ? handleAttendanceApproval
              : handleTaskApproval;

          const approveAction =
            activeTab === APPROVAL_TABS.BILLS.value ? "approve" : "approved";
          const rejectAction =
            activeTab === APPROVAL_TABS.BILLS.value ? "reject" : "rejected";

          return (
            <div className="flex gap-6 items-center">
              {[
                STATUS.PENDING_L1_APPROVAL,
                STATUS.PENDING_L2_APPROVAL,
              ].includes(rowData.status) && (
                <>
                  <RoleValidator
                    allowedRoles={[
                      ROLES.PROJECT_ENGINEER,
                      ROLES.PROJECT_MANAGER,
                    ]}
                  >
                    <Tooltip text="Approve">
                      <FaCheck
                        size={18}
                        className="cursor-pointer text-green-600"
                        onClick={() => handleAction(rowData.id, approveAction)}
                      />
                    </Tooltip>
                  </RoleValidator>

                  <RoleValidator
                    allowedRoles={[
                      ROLES.PROJECT_ENGINEER,
                      ROLES.PROJECT_MANAGER,
                    ]}
                  >
                    <Tooltip text="Reject">
                      <FaTimes
                        size={18}
                        className="cursor-pointer text-red-600"
                        onClick={() => handleAction(rowData.id, rejectAction)}
                      />
                    </Tooltip>
                  </RoleValidator>
                </>
              )}
            </div>
          );
        }
        return null;

      default:
        return rowData[key];
    }
  };

  const renderMobileCards = () => (
    <ul className="space-y-3">
      {approvalDetails.length > 0 ? (
        approvalDetails.map((item) => {
          return (
            <li
              key={item.id}
              className="p-3 rounded-lg border text-sm grid grid-cols-6 gap-2 shadow-sm items-center"
            >
              <div className="col-span-3">
                <div className="flex items-center gap-2 w-full mb-1 min-w-0">
                  {activeTab === APPROVAL_TABS.BILLS.value ? (
                    <span className="font-medium truncate flex-1">
                      {formatAmountINR(item.amount)}
                    </span>
                  ) : activeTab === APPROVAL_TABS.ATTENDANCE.value ? (
                    <span className="font-medium truncate flex-1">
                      {item.site}
                    </span>
                  ) : (
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="font-medium truncate block">
                        {item.title}
                      </span>
                      <span
                        className={`flex-shrink-0 text-[10px] font-medium px-2 py-[2px] rounded-full ${
                          item.super_task
                            ? "bg-blue-100 text-blue-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {item.super_task ? "Sub-task" : "Task"}
                      </span>
                    </div>
                  )}
                </div>

                <div className="text-xs text-gray-600 space-y-0.7">
                  {activeTab === APPROVAL_TABS.BILLS.value ? (
                    <>
                      <div className="font-medium truncate">{item.site}</div>
                      <div className="font-medium truncate">
                        {toProperCase(item.category)}
                      </div>
                      <div className="font-medium truncate">
                        {formatDateDDMMYYYY(item.date)}
                      </div>
                    </>
                  ) : activeTab === APPROVAL_TABS.ATTENDANCE.value ? (
                    <div className="flex flex-col md:flex-row flex-wrap md:gap-2 text-xs">
                      <div className="font-medium truncate">
                        {formatDateDDMMYYYY(item.date)}
                      </div>
                      <div className="flex">
                        {item?.events?.map((event, index) => (
                          <React.Fragment key={event.id}>
                            <div className="flex items-center text-green-700">
                              {formatTimeAMPM(event?.time).toUpperCase()}
                            </div>
                            {item?.events.length === 2 && index === 0 && (
                              <div className="px-1"> - </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1">
                        <div className="font-medium truncate-[90%]">
                          {item.site_name}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-row justify-start col-span-2">
                <div
                  className={`inline-flex items-center mt-1 px-2 py-1 rounded-md text-xs text-gray-600 ${
                    getStatusIconAndColor(item.status).color
                  }`}
                >
                  <span>{TASK_STATUS_LABELS[item.status]}</span>
                </div>
              </div>

              <div className="flex flex-col col-span-1 text-center gap-2">
                {[
                  APPROVAL_TABS.BILLS.value,
                  APPROVAL_TABS.ATTENDANCE.value,
                  APPROVAL_TABS.TASKS.value,
                ].includes(activeTab) && (
                  <div className="flex flex-row gap-3 w-full justify-end">
                    {[
                      STATUS.PENDING_L1_APPROVAL,
                      STATUS.PENDING_L2_APPROVAL,
                    ].includes(item.status) && (
                      <>
                        <RoleValidator
                          allowedRoles={[
                            ROLES.PROJECT_ENGINEER,
                            ROLES.PROJECT_MANAGER,
                          ]}
                        >
                          <Tooltip text="Approve">
                            <FaCheck
                              size={18}
                              className="cursor-pointer text-green-600"
                              onClick={() =>
                                (activeTab === APPROVAL_TABS.BILLS.value
                                  ? handleBillApproval
                                  : activeTab === APPROVAL_TABS.ATTENDANCE.value
                                  ? handleAttendanceApproval
                                  : handleTaskApproval)(item.id, "approve")
                              }
                            />
                          </Tooltip>
                        </RoleValidator>

                        <RoleValidator
                          allowedRoles={[
                            ROLES.PROJECT_ENGINEER,
                            ROLES.PROJECT_MANAGER,
                          ]}
                        >
                          <Tooltip text="Reject">
                            <FaTimes
                              size={18}
                              className="cursor-pointer text-red-600"
                              onClick={() =>
                                (activeTab === APPROVAL_TABS.BILLS.value
                                  ? handleBillApproval
                                  : activeTab === APPROVAL_TABS.ATTENDANCE.value
                                  ? handleAttendanceApproval
                                  : handleTaskApproval)(item.id, "reject")
                              }
                            />
                          </Tooltip>
                        </RoleValidator>
                      </>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })
      ) : (
        <div className="w-full h-[280px] flex justify-center items-center text-gray-300 flex-col">
          <ClipboardList size={35} />
          <span>No {activeTab}</span>
        </div>
      )}
    </ul>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-4 py-4"
    >
      <div className="flex justify-between mb-4 mt-4 md:mt-0">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          {ROLES.SITE_ENGINEER.includes(user.role)
            ? "My Approval Actions"
            : "Approval Actions"}{" "}
        </h2>
        <div className="flex gap-3 border-b border-gray-200">
          {Object.values(APPROVAL_TABS).map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setActiveTab(tab.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className={`px-3 py-2 text-sm font-medium ${
                activeTab === tab.value
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-4">
        <FilterCard
          tempFilters={filters}
          setTempFilters={setFilters}
          onFilterChange={(updated) => {
            setFilters(updated); // apply filters
            setPagination((prev) => ({ ...prev, page: 1 })); // reset to first page
          }}
          fields={[
            {
              key: "project",
              label: "Project",
              type: "select",
              options: [
                { value: "", label: "All Projects" },
                ...projects.map((p) => ({ value: p.id, label: p.name })),
              ],
            },
            {
              key: "site",
              label: "Site",
              type: "select",
              options: [{ value: "", label: "All Sites" }, ...sites],
            },
            { key: "date", label: "Date", type: "date" },
          ]}
        />
      </div>

      {isRestrictedRole ? (
        <div className="flex justify-center items-center h-[60vh] text-gray-400">
          No approvals available
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center h-[60vh]">
          <Spinner />
        </div>
      ) : (
        <div>
          {/* Desktop View */}
          <div className="hidden h-[54vh] md:block overflow-y-auto">
            <GenericTable
              tableColumns={
                activeTab === APPROVAL_TABS.BILLS.value
                  ? APPROVAL_BILL_COLUMNS
                  : activeTab === APPROVAL_TABS.TASKS.value
                  ? TASK_COLUMNS
                  : APPROVAL_ATTENDANCE_COLUMNS
              }
              tableRows={approvalDetails}
              columnLabels={
                activeTab === APPROVAL_TABS.BILLS.value
                  ? APPROVAL_BILL_LABELS
                  : activeTab === APPROVAL_TABS.TASKS.value
                  ? TASK_LABELS
                  : APPROVAL_ATTENDANCE_LABELS
              }
              renderField={renderFields}
            />
          </div>

          {/* Mobile View */}
          <div className="block md:hidden h-[60vh] overflow-y-auto">
            {renderMobileCards()}
          </div>
          <Pagination
            page={pagination.page}
            rowsPerPage={pagination.limit}
            totalPages={Math.max(
              1,
              Math.ceil(pagination.count / pagination.limit)
            )}
            onPageChange={(newPage) =>
              setPagination((prev) => ({ ...prev, page: newPage }))
            }
            onPageSizeChange={(newLimit) =>
              setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }))
            }
          />
        </div>
      )}
    </motion.div>
  );
};

export default ApprovalListPage;
